import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { CityDatalist } from "@/components/CityDatalist";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CATEGORIES, PLATFORMS, LANGUAGES, CREATOR_TIERS, CURRENCIES, ACCOUNT_TYPES, COUNTRIES, tierFromFollowers, tierLabel } from "@/lib/constants";
import { toast } from "sonner";
import { BadgeCheck, Instagram, Youtube, Facebook, Linkedin, Twitter, Ghost, Music2, Plug, ShieldCheck, Save, X, Twitch, Pin } from "lucide-react";
import { PUBLIC_STATS_PLATFORMS } from "@/lib/constants";

const PLATFORM_META = {
  Instagram: { key: "instagram", Icon: Instagram, color: "text-neon-pink", placeholder: "https://instagram.com/yourhandle" },
  YouTube: { key: "youtube", Icon: Youtube, color: "text-destructive", placeholder: "https://youtube.com/@yourchannel" },
  Facebook: { key: "facebook", Icon: Facebook, color: "text-neon-cyan", placeholder: "https://facebook.com/yourpage" },
  X: { key: "x", Icon: Twitter, color: "text-foreground", placeholder: "https://x.com/yourhandle" },
  LinkedIn: { key: "linkedin", Icon: Linkedin, color: "text-neon-purple", placeholder: "https://linkedin.com/in/you" },
  Snapchat: { key: "snapchat", Icon: Ghost, color: "text-brand-orange", placeholder: "https://snapchat.com/add/you" },
  TikTok: { key: "tiktok", Icon: Music2, color: "text-foreground", placeholder: "https://tiktok.com/@yourhandle" },
  Twitch: { key: "twitch", Icon: Twitch, color: "text-neon-purple", placeholder: "https://twitch.tv/yourchannel" },
  Pinterest: { key: "pinterest", Icon: Pin, color: "text-destructive", placeholder: "https://pinterest.com/yourhandle" },
};

const fmtNum = (n) => (n == null ? "—" : Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n));

function PublicStats({ v, k }) {
  const labels = { twitch: ["Followers", "Videos", null], pinterest: ["Followers", "Pins", "Following"], facebook: ["Followers", "Page likes", null] };
  const [l1, l2, l3] = labels[k] || ["Followers", k === "x" ? "Posts" : "Videos", k === "tiktok" ? "Likes" : "Following"];
  const second = k === "facebook" ? v.likes : v.posts;
  const third = k === "tiktok" ? v.likes : v.following;
  return (
    <div className="mt-3 flex items-center gap-3" data-testid={`${k}-verified-stats`}>
      {v.thumbnail && <img src={v.thumbnail} alt="" className="h-12 w-12 rounded-full object-cover border border-border" referrerPolicy="no-referrer" />}
      <div className="grid grid-cols-3 gap-2 flex-1">
        {[[v.followers, l1], [second, l2], [third, l3]].filter(([, l]) => l).map(([n, l]) => (
          <div key={l} className="rounded-lg bg-surface border border-border p-2 text-center"><p className="font-heading font-black text-sm">{fmtNum(n)}</p><p className="text-[10px] text-muted-foreground">{l}</p></div>
        ))}
      </div>
      <p className="sr-only">{v.source}</p>
    </div>
  );
}

function Chips({ options, selected, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const active = selected?.includes(o);
        return (
          <button type="button" key={o} onClick={() => onToggle(o)}
            data-testid={`${testid}-${o.toLowerCase().replace(/\s+/g, "-")}`}
            className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${
              active ? "bg-neon-lime text-background border-neon-lime font-semibold" : "border-border bg-background text-muted-foreground hover:text-foreground hover:border-white/20"
            }`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user.name || "",
    currency: user.currency || "USD",
    bio: user.bio || "",
    location: user.location || "",
    account_type: user.account_type || "Influencer",
    country: user.country || "",
    city: user.city || "",
    company_name: user.company_name || "",
    website: user.website || "",
    categories: user.categories || [],
    platforms: user.platforms || [],
    languages: user.languages || [],
    followers: user.followers || "",
    engagement_rate: user.engagement_rate || "",
    creator_type: user.creator_type || "",
    age: user.age ?? "",
    content_specialties: user.content_specialties || "",
    audience_demographics: user.audience_demographics || "",
    starting_rate: user.starting_rate || "",
    social_links: user.social_links || {},
  });
  const [saving, setSaving] = useState(false);
  const isInfluencer = user.role === "influencer";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, social_links: { ...f.social_links, [k]: v } }));
  const onFollowers = (v) => setForm((f) => ({ ...f, followers: v, creator_type: f.creator_type || tierFromFollowers(v) }));
  const [verified, setVerified] = useState(user.social_verified || {});
  const [connecting, setConnecting] = useState(null);
  const [showcase, setShowcase] = useState(user.showcase || []);
  const [scUrl, setScUrl] = useState("");
  const [scBusy, setScBusy] = useState(false);
  const addShowcase = async () => {
    if (!scUrl.trim()) { toast.error("Paste a post/video link."); return; }
    setScBusy(true);
    try {
      const { data } = await api.post("/social/showcase", { url: scUrl.trim() });
      setShowcase((s) => [data, ...s]);
      setScUrl("");
      toast.success("Added to your showcase!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch that link.");
    } finally { setScBusy(false); }
  };
  const removeShowcase = async (id) => {
    try { const { data } = await api.delete(`/social/showcase/${id}`); setShowcase(data.showcase); } catch (e) {}
  };
  // fmtNum now defined at module scope

  const connectYouTube = async () => {
    const url = form.social_links.youtube;
    if (!url) { toast.error("Paste your YouTube channel URL or @handle first."); return; }
    setConnecting("youtube");
    try {
      const { data } = await api.post("/social/youtube/connect", { url });
      setVerified((v) => ({ ...v, youtube: data }));
      setSocial("youtube", data.url);
      setUser({ ...user, social_verified: { ...(user.social_verified || {}), youtube: data }, social_links: { ...(user.social_links || {}), youtube: data.url } });
      toast.success(`YouTube verified — ${data.title}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't verify this channel.");
    } finally { setConnecting(null); }
  };

  const connectInstagram = async () => {
    const val = form.social_links.instagram;
    if (!val) { toast.error("Paste your Instagram username or profile URL first."); return; }
    setConnecting("instagram");
    try {
      const { data } = await api.post("/social/instagram/connect", { username: val });
      setVerified((v) => ({ ...v, instagram: data }));
      setSocial("instagram", data.url);
      setUser({ ...user, social_verified: { ...(user.social_verified || {}), instagram: data }, social_links: { ...(user.social_links || {}), instagram: data.url } });
      toast.success(`Instagram verified — @${data.username}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't verify this Instagram profile.");
    } finally { setConnecting(null); }
  };

  const connectPublic = async (key, label) => {
    const val = form.social_links[key];
    if (!val) { toast.error(`Paste your ${label} username or profile URL first.`); return; }
    setConnecting(key);
    try {
      const { data } = await api.post(`/social/public/${key}/connect`, { username: val });
      setVerified((v) => ({ ...v, [key]: data }));
      setSocial(key, data.url);
      setUser({ ...user, social_verified: { ...(user.social_verified || {}), [key]: data }, social_links: { ...(user.social_links || {}), [key]: data.url } });
      toast.success(`${label} stats fetched — @${data.username}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Couldn't fetch ${label} stats.`);
    } finally {
      setConnecting(null);
    }
  };

  const onConnect = (platform) => {
    if (platform === "YouTube") return connectYouTube();
    if (platform === "Instagram") return connectInstagram();
    if (platform === "Facebook") return igOwnerConnect();
    const key = PLATFORM_META[platform]?.key;
    if (PUBLIC_STATS_PLATFORMS.includes(key)) return connectPublic(key, platform);
    toast.info(`${platform} doesn't allow reading public stats. Your link is saved as self-reported.`);
  };

  const igOwnerConnect = async () => {
    try {
      const { data } = await api.get("/social/instagram/oauth/start", { params: { origin: window.location.origin } });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start Instagram verification.");
    }
  };

  const ytOwnerConnect = async () => {
    try {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const { data } = await api.get("/social/youtube/oauth/start", { params: { origin: window.location.origin } });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start YouTube verification.");
    }
  };

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("yt") !== "connected") return;
    api.get("/auth/me").then(({ data }) => { setUser(data); setVerified(data.social_verified || {}); }).catch(() => {});
    window.history.replaceState(null, "", window.location.pathname);
  }, [setUser]);

  useEffect(() => {
    const ig = new URLSearchParams(window.location.search).get("ig");
    if (!ig) return;
    if (ig === "connected") {
      toast.success("Instagram ownership verified!");
      api.get("/auth/me").then(({ data }) => { setUser(data); setVerified(data.social_verified || {}); }).catch(() => {});
    } else if (ig === "fbonly") {
      toast.success("Facebook Page verified! (No Instagram account is linked to it yet.)");
      api.get("/auth/me").then(({ data }) => { setUser(data); setVerified(data.social_verified || {}); }).catch(() => {});
    } else if (ig === "noig") {
      toast.error("No Facebook Page found on your account. Create or link a Page, then try again.");
    } else {
      toast.error("Instagram verification was cancelled or failed.");
    }
    window.history.replaceState(null, "", window.location.pathname);
  }, [setUser]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        currency: form.currency, bio: form.bio, location: form.location,
        country: form.country, city: form.city,
      };
      if (isInfluencer) {
        Object.assign(payload, {
          account_type: form.account_type,
          categories: form.categories, platforms: form.platforms, languages: form.languages,
          followers: form.followers ? Number(form.followers) : null,
          engagement_rate: form.engagement_rate ? Number(form.engagement_rate) : null,
          creator_type: form.creator_type, content_specialties: form.content_specialties,
          age: form.age ? Number(form.age) : null,
          audience_demographics: form.audience_demographics,
          starting_rate: form.starting_rate ? Number(form.starting_rate) : null,
          social_links: form.social_links,
        });
      } else {
        Object.assign(payload, { company_name: form.company_name, website: form.website });
      }
      const res = await api.put("/profile", payload);
      setUser(res.data);
      toast.success("Profile saved!");
    } catch (e) {
      toast.error("Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-5 mb-8">
          <img src={user.picture} alt="" className="h-20 w-20 rounded-2xl object-cover bg-muted" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading font-black text-3xl tracking-tight">{user.name}</h1>
              {user.verified && <BadgeCheck className="h-6 w-6 text-neon-cyan" />}
            </div>
            <Badge variant="outline" className="capitalize mt-1 border-neon-lime/30 text-neon-lime bg-neon-lime/10">{user.role}</Badge>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-border bg-surface p-7 space-y-6">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="mb-2 block">Preferred currency</Label>
              <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger data-testid="profile-currency-select" className="bg-background border-border h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Location (short)</Label>
              <Input value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="profile-location-input" placeholder="e.g. Mumbai, India" className="bg-background border-border h-11" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="mb-2 block">Country</Label>
              <Select value={form.country} onValueChange={(v) => set("country", v)}>
                <SelectTrigger data-testid="profile-country-select" className="bg-background border-border h-11"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">City</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} list="city-options" autoComplete="off" data-testid="profile-city-input" placeholder="Start typing, e.g. Mumbai" className="bg-background border-border h-11" />
              <CityDatalist />
            </div>
          </div>

          <div>
            <Label className="mb-2 block">{isInfluencer ? "Bio" : "About your brand"}</Label>
            <Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} data-testid="profile-bio-input" className="bg-background border-border min-h-[90px]" />
          </div>

          {!isInfluencer && (
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="mb-2 block">Company / Brand name</Label>
                <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="profile-company-input" className="bg-background border-border h-11" />
              </div>
              <div>
                <Label className="mb-2 block">Website</Label>
                <Input value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="profile-website-input" placeholder="https://" className="bg-background border-border h-11" />
              </div>
            </div>
          )}

          {isInfluencer && (
            <>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="mb-2 block">Followers</Label>
                  <Input type="number" value={form.followers} onChange={(e) => onFollowers(e.target.value)} data-testid="profile-followers-input" placeholder="e.g. 45000" className="bg-background border-border h-11" />
                  {form.followers ? <p className="text-xs text-neon-lime mt-1">Suggested tier: {tierLabel(tierFromFollowers(form.followers))}</p> : null}
                </div>
                <div>
                  <Label className="mb-2 block">Engagement %</Label>
                  <Input type="number" value={form.engagement_rate} onChange={(e) => set("engagement_rate", e.target.value)} data-testid="profile-engagement-input" placeholder="e.g. 4.5" className="bg-background border-border h-11" />
                </div>
                <div>
                  <Label className="mb-2 block">Your age</Label>
                  <Input type="number" value={form.age} onChange={(e) => set("age", e.target.value)} data-testid="profile-age-input" placeholder="e.g. 27" className="bg-background border-border h-11" />
                </div>
                <div>
                  <Label className="mb-2 block">Price per video</Label>
                  <Input type="number" value={form.starting_rate} onChange={(e) => set("starting_rate", e.target.value)} data-testid="profile-rate-input" placeholder="e.g. 5000" className="bg-background border-border h-11" />
                  <p className="text-xs text-muted-foreground mt-1">Your rate for one collaboration post/video.</p>
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Account type</Label>
                <Select value={form.account_type} onValueChange={(v) => set("account_type", v)}>
                  <SelectTrigger data-testid="profile-accounttype-select" className="bg-background border-border h-11"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>{ACCOUNT_TYPES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-2 block">Creator tier</Label>
                <Select value={form.creator_type} onValueChange={(v) => set("creator_type", v)}>
                  <SelectTrigger data-testid="profile-creatortype-select" className="bg-background border-border h-11"><SelectValue placeholder="Select tier" /></SelectTrigger>
                  <SelectContent>{CREATOR_TIERS.map((c) => <SelectItem key={c.name} value={c.name}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-3 block">Categories</Label>
                <Chips options={CATEGORIES} selected={form.categories} onToggle={(v) => toggle("categories", v)} testid="profile-categories" />
              </div>
              <div>
                <Label className="mb-1 block">Platforms & social profiles</Label>
                <p className="text-xs text-muted-foreground mb-3">Select the platforms you create on, then add your profile link for each.</p>
                <div className="flex flex-wrap gap-2 mb-4" data-testid="profile-platforms">
                  {PLATFORMS.map((p) => {
                    const active = form.platforms.includes(p);
                    return (
                      <button type="button" key={p} onClick={() => toggle("platforms", p)}
                        data-testid={`profile-platforms-${p.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${active ? "bg-neon-lime text-background border-neon-lime font-semibold" : "border-border bg-background text-muted-foreground hover:text-foreground hover:border-white/20"}`}>
                        {p}
                      </button>
                    );
                  })}
                </div>
                <div className="space-y-3">
                  {form.platforms.map((p) => {
                    const meta = PLATFORM_META[p];
                    if (!meta) return null;
                    const { key, Icon, color, placeholder } = meta;
                    const v = verified[key];
                    const isYT = p === "YouTube";
                    const isIG = p === "Instagram";
                    const isPublic = PUBLIC_STATS_PLATFORMS.includes(key) || key === "facebook";
                    const canVerify = isYT || isIG || isPublic;
                    const busy = connecting === key;
                    return (
                      <div key={p} className="rounded-xl border border-border bg-background p-3" data-testid={`platform-row-${key}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className={`h-5 w-5 ${color}`} />
                          <span className="font-semibold text-sm">{p}</span>
                          {v ? (
                            <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-neon-lime" data-testid={`verified-badge-${key}`}><BadgeCheck className="h-3.5 w-3.5" /> API-verified</span>
                          ) : (
                            <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5" /> Self-reported</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Input value={form.social_links[key] || ""} onChange={(e) => setSocial(key, e.target.value)} data-testid={`social-${key}-input`} placeholder={isIG ? "your_username or profile URL" : placeholder} className="bg-surface border-border h-10" />
                          <Button type="button" variant={canVerify ? "default" : "outline"} disabled={busy} onClick={() => onConnect(p)} data-testid={`connect-${key}-btn`}
                            className={`shrink-0 rounded-full h-10 px-4 text-xs ${canVerify ? "bg-brand-gradient text-white border-0" : "border-border"}`}>
                            <Plug className="h-3.5 w-3.5 mr-1" /> {busy ? "Verifying…" : key === "facebook" ? (v ? "Refresh" : "Verify Page") : canVerify ? (v ? "Refresh" : "Verify") : "Connect"}
                          </Button>
                        </div>
                        {isYT && v && (
                          <div className="mt-3 grid grid-cols-3 gap-2" data-testid="youtube-verified-stats">
                            <div className="rounded-lg bg-surface border border-border p-2 text-center"><p className="font-heading font-black text-sm">{fmtNum(v.subscribers)}</p><p className="text-[10px] text-muted-foreground">Subscribers</p></div>
                            <div className="rounded-lg bg-surface border border-border p-2 text-center"><p className="font-heading font-black text-sm">{fmtNum(v.views)}</p><p className="text-[10px] text-muted-foreground">Views</p></div>
                            <div className="rounded-lg bg-surface border border-border p-2 text-center"><p className="font-heading font-black text-sm">{fmtNum(v.videos)}</p><p className="text-[10px] text-muted-foreground">Videos</p></div>
                            <p className="col-span-3 text-[10px] text-muted-foreground">Verified via {v.source} · synced {new Date(v.last_synced).toLocaleDateString()}</p>
                          </div>
                        )}
                        {isIG && v && (
                          <div className="mt-3 flex items-center gap-3" data-testid="instagram-verified-stats">
                            {v.thumbnail && <img src={v.thumbnail} alt="" className="h-12 w-12 rounded-full object-cover border border-border" referrerPolicy="no-referrer" />}
                            <div className="grid grid-cols-3 gap-2 flex-1">
                              <div className="rounded-lg bg-surface border border-border p-2 text-center"><p className="font-heading font-black text-sm">{fmtNum(v.followers)}</p><p className="text-[10px] text-muted-foreground">Followers</p></div>
                              <div className="rounded-lg bg-surface border border-border p-2 text-center"><p className="font-heading font-black text-sm">{fmtNum(v.posts)}</p><p className="text-[10px] text-muted-foreground">Posts</p></div>
                              <div className="rounded-lg bg-surface border border-border p-2 text-center"><p className="font-heading font-black text-sm">{fmtNum(v.following)}</p><p className="text-[10px] text-muted-foreground">Following</p></div>
                            </div>
                          </div>
                        )}
                        {isPublic && v && <PublicStats v={v} k={key} />}
                        {key === "facebook" && !v && <p className="mt-2 text-xs text-muted-foreground">Log in with Facebook to verify a Page you manage (followers & likes). Personal profiles can't be verified.</p>}
                        {isYT && (
                          <button type="button" onClick={ytOwnerConnect} data-testid="connect-youtube-owner-btn" className="mt-2 text-xs text-neon-cyan hover:underline inline-flex items-center gap-1">
                            <ShieldCheck className="h-3.5 w-3.5" /> {v?.owner_verified ? "Ownership verified · re-check with Google" : "Own this channel? Verify ownership with Google"}
                          </button>
                        )}
                        {isIG && (
                          <button type="button" onClick={igOwnerConnect} data-testid="connect-instagram-owner-btn" className="mt-2 text-xs text-neon-cyan hover:underline inline-flex items-center gap-1">
                            <ShieldCheck className="h-3.5 w-3.5" /> Own this account? Verify ownership via Facebook
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {form.platforms.length === 0 && <p className="text-sm text-muted-foreground">No platforms selected yet.</p>}
                </div>
              </div>
              <div>
                <Label className="mb-3 block">Languages</Label>
                <Chips options={LANGUAGES} selected={form.languages} onToggle={(v) => toggle("languages", v)} testid="profile-languages" />
              </div>
              <div>
                <Label className="mb-2 block">Content specialties</Label>
                <Input value={form.content_specialties} onChange={(e) => set("content_specialties", e.target.value)} data-testid="profile-specialties-input" placeholder="e.g. Short-form reels, product unboxing" className="bg-background border-border h-11" />
              </div>
              <div>
                <Label className="mb-2 block">Audience demographics</Label>
                <Input value={form.audience_demographics} onChange={(e) => set("audience_demographics", e.target.value)} data-testid="profile-audience-input" placeholder="e.g. 70% female, 18-34, India metros" className="bg-background border-border h-11" />
              </div>
              <div className="rounded-xl border border-border bg-background p-4 flex items-center justify-between gap-4" data-testid="public-visibility-row">
                <div>
                  <p className="font-semibold text-sm">Hide my profile from public pages</p>
                  <p className="text-xs text-muted-foreground">When on, you won't appear on public influencer pages, your public profile link or search engines. Brands signed in to InfluencerCollab can still find you.</p>
                </div>
                <Switch checked={!!user.public_hidden} data-testid="public-hidden-switch" onCheckedChange={async (v) => {
                  try { const { data } = await api.put("/profile", { public_hidden: v }); setUser(data); toast.success(v ? "Your profile is hidden from public pages." : "Your profile is visible on public pages."); }
                  catch (e) { toast.error("Could not update visibility."); }
                }} />
              </div>
              <div>
                <Label className="mb-1 block">Content showcase</Label>
                <p className="text-xs text-muted-foreground mb-3">Paste any public post / reel / video link — we'll pull its thumbnail to show your best work (TikTok, X, Facebook, Pinterest, Twitch, and more).</p>
                <div className="flex items-center gap-2 mb-3">
                  <Input value={scUrl} onChange={(e) => setScUrl(e.target.value)} data-testid="showcase-url-input" placeholder="https://www.tiktok.com/@you/video/…" className="bg-background border-border h-11" onKeyDown={(e) => e.key === "Enter" && addShowcase()} />
                  <Button type="button" onClick={addShowcase} disabled={scBusy} data-testid="showcase-add-btn" className="shrink-0 rounded-full h-11 px-5 bg-neon-lime text-background font-bold">{scBusy ? "Adding…" : "Add"}</Button>
                </div>
                {showcase.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="showcase-grid">
                    {showcase.map((it) => (
                      <div key={it.id} className="relative rounded-xl overflow-hidden border border-border bg-background group">
                        {it.thumbnail ? <img src={it.thumbnail} alt="" className="w-full h-32 object-cover" referrerPolicy="no-referrer" /> : <div className="w-full h-32 flex items-center justify-center text-muted-foreground text-xs">No preview</div>}
                        <button type="button" onClick={() => removeShowcase(it.id)} data-testid={`showcase-remove-${it.id}`} className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="h-3.5 w-3.5" /></button>
                        <div className="p-2"><p className="text-[10px] uppercase tracking-wide text-neon-pink font-bold">{it.platform}</p>{it.title && <p className="text-xs line-clamp-2 mt-0.5">{it.title}</p>}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <Button onClick={save} disabled={saving} data-testid="save-profile-btn" className="rounded-full bg-neon-lime text-background hover:bg-neon-lime/90 font-bold h-11 px-8">
            <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save profile"}
          </Button>
        </motion.div>
      </div>
    </AppShell>
  );
}
